import { getRecentActions } from './actionLog';

export type FEErrorEvent = {
  origin: 'frontend';
  language?: 'ts' | 'tsx' | string;
  severity?: 'error' | 'warn' | 'fatal';
  message: string;
  stack?: string;
  component?: string;
  file?: string;
  line?: number;
  column?: number;
  url?: string;
  method?: string;
  status?: number;
  request_id?: string;
  user_id?: number;
  session_token?: string;
  tags?: Record<string, string>;
  context?: Record<string, any>;
  env?: string;
  version?: string;
  hostname?: string;
  occurred_at?: string;
};

function readLS(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function getIngestUrl(): string | null {
  if (process.env.REACT_APP_ERROR_INGEST_URL) return process.env.REACT_APP_ERROR_INGEST_URL as string;
  try {
    if (typeof window !== 'undefined') {
      const host = window.location.hostname || '';
      const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1' || /^[0-9.]+$/.test(host) || host.endsWith('.local');
      if (!isLocal) {
        return 'https://errors.tdvorak.dev/api/v1/errors';
      }
    }
  } catch {}
  return '/api/v1/errors';
}

function getIngestToken(): string | null {
  return (process.env.REACT_APP_ERROR_INGEST_TOKEN as string) || null;
}

let lastSentAt = 0;
let lastHash = '';

function fingerprint(ev: FEErrorEvent): string {
  const basis = [ev.message, ev.stack || '', ev.url || '', ev.component || ''].join('|');
  let h = 0;
  for (let i = 0; i < basis.length; i++) {
    h = ((h << 5) - h) + basis.charCodeAt(i);
    h |= 0;
  }
  return String(h);
}

export async function reportError(ev: Partial<FEErrorEvent>): Promise<void> {
  const url = getIngestUrl();
  if (!url) return; // disabled until configured

  const now = Date.now();
  const full: FEErrorEvent = {
    origin: 'frontend',
    language: ev.language || 'tsx',
    severity: ev.severity || 'error',
    message: ev.message || 'Unknown error',
    stack: ev.stack,
    component: ev.component,
    file: ev.file,
    line: ev.line,
    column: ev.column,
    url: ev.url || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : undefined),
    method: ev.method,
    status: ev.status,
    request_id: ev.request_id,
    user_id: ev.user_id,
    session_token: ev.session_token,
    tags: {
      service: 'frontend',
      instance_env: String(process.env.NODE_ENV || ''),
      instance_host: (typeof window !== 'undefined' && window.location && window.location.hostname) ? window.location.hostname : '',
      ...(ev.tags || {}),
    },
    context: {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      viewport: typeof window !== 'undefined' ? { w: window.innerWidth, h: window.innerHeight } : undefined,
      recentActions: getRecentActions(18),
      ...ev.context,
    },
    env: ev.env || process.env.NODE_ENV,
    version: ev.version,
    hostname: ev.hostname || (typeof window !== 'undefined' ? window.location.hostname : undefined),
    occurred_at: new Date(now).toISOString(),
  };

  const hash = fingerprint(full);
  if (hash === lastHash && (now - lastSentAt) < 1500) {
    return; // basic de-dupe burst
  }
  lastHash = hash;
  lastSentAt = now;

  try {
    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(getIngestToken() ? { 'Authorization': `Bearer ${getIngestToken()}` } : {}),
      },
      body: JSON.stringify(full),
      credentials: 'omit',
      keepalive: true,
    });
  } catch {
    // swallow
  }
}

export function installGlobalErrorHandlers() {
  if (typeof window === 'undefined') return;
  // window.onerror
  window.addEventListener('error', (event: ErrorEvent) => {
    const isResourceError = (event as any).message === undefined && event.filename === '';
    const message = (event.message || (isResourceError ? 'Resource load error' : 'Unhandled error')) as string;
    reportError({
      message,
      stack: event.error?.stack,
      file: event.filename,
      line: event.lineno,
      column: event.colno,
      url: window.location.pathname + window.location.search,
    });
  });
  // unhandledrejection
  window.addEventListener('unhandledrejection', (event) => {
    const reason: any = (event as any).reason;
    const message = typeof reason === 'string' ? reason : (reason?.message || 'Unhandled rejection');
    const stack = typeof reason === 'object' ? (reason?.stack || '') : '';
    reportError({ message, stack, url: window.location.pathname + window.location.search });
  });
}
